import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';
import {addResultCases} from 'utils/helpers';
import {DashboardBrand, ListResult} from 'utils/types';
import {
  WithOrderingURLParams,
  WithSearchURLParams,
  WithPaginationURLParams,
  withOrderingURLParams,
  withSearchURLParams,
  withPaginationURLParams,
} from 'utils/urlParamHelpers';

type BrandListParams = WithOrderingURLParams<WithSearchURLParams<WithPaginationURLParams>>;
const serializeBrandListParams = withOrderingURLParams(withSearchURLParams(withPaginationURLParams()));

export interface IState {
  brands: ListResult<DashboardBrand>;
  brandToEdit: DashboardBrand | null;
}

export function buildBrandFormData(data: {name: string; image?: File | null}): FormData {
  const formData = new FormData();
  formData.append('name', data.name);
  if (data.image) {
    formData.append('image', data.image);
  } else if (data.image === null) {
    formData.append('image', '');
  }
  return formData;
}

export const brandEndpoints = {
  createBrand: createAsyncThunk('brand/createBrand', async (formData: FormData, {rejectWithValue}) => {
    try {
      const response = await ApiService.post('/dashboard_brand/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }),
  getBrandList: createAsyncThunk('brand/getBrandList', async (payload: BrandListParams) => {
    const params = new URLSearchParams();
    serializeBrandListParams(params, payload);
    const response = await ApiService.get(`/dashboard_brand/?${params.toString()}`);
    return response.data;
  }),
  updateBrand: createAsyncThunk(
    'brand/updateBrand',
    async ({id, formData}: {id: number | string; formData: FormData}, {rejectWithValue}) => {
      try {
        const response = await ApiService.patch(`/dashboard_brand/${id}/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response.data);
      }
    },
  ),
  deleteBrand: createAsyncThunk('brand/deleteBrand', async (id: number | string, {rejectWithValue}) => {
    try {
      await ApiService.delete(`/dashboard_brand/${id}/`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }),
};

export const brandSlice = createSlice({
  name: 'brand',
  initialState: {
    brands: {as: 'idle'},
    brandToEdit: null,
  } as IState,
  reducers: {
    setBrandToEdit(state, action) {
      state.brandToEdit = action.payload;
    },
  },
  extraReducers: builder => {
    addResultCases(builder, brandEndpoints.getBrandList, 'brands');
  },
});

export const brandActions = brandSlice.actions;

export type BrandSlice = {
  [brandSlice.name]: ReturnType<(typeof brandSlice)['reducer']>;
};
